import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { createAttachment } from "../../../../../../apis";
import { InvestigationButton } from "../../components/InvestigationButton";
import { RichTextEditor } from "../../../../../MainComponents/RichTextEditor/RichTextEditor";
import { ModalContainer } from "../../components/ModalContainer";
import {
  DragAndDropFiles,
  GenerateWithIA,
  TemporalUploadedFile,
} from "../../components/InvestigationComponents";
import { AddConclusionWithIA } from "./AddConclusionWithIA";

// Funcion revisar el formulario y retornar si es valido y cuales son los errores
const validateForm = (form) => {
  const { conclusion } = form;
  const errors = {};
  //Conclusion
  const text = conclusion.replace(/<[^>]*>/g, "").trim();
  if (text.length === 0) {
    errors.conclusion = true;
  }
  return [Object.entries(errors).length === 0 ? true : false, errors];
};

// Modal de carga de preguntas para entrevistas
export const AddConclusion = ({
  show,
  onHide,
  idx,
  interviews,
  handleSaveInterviews,
  alias,
  interviewer,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const [conclusion, setConclusion] = useState("");
  const [files, setFiles] = useState([]);

  const [showAddConclusionWithIA, setShowAddConclusionWithIA] = useState(false);

  const [triedToSave, setTriedToSave] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset values
  useEffect(() => {
    if (!show) {
      setTimeout(() => {
        setConclusion("");
        setFiles([]);
        setTriedToSave(false);
        setErrors({});
      }, 200);
    }
  }, [show]);

  // Validar cada que se hace un cambio
  useEffect(() => {
    if (triedToSave) {
      const [_, errors] = validateForm({ conclusion });
      setErrors(errors);
    }
  }, [conclusion]);

  const handleAddAttachment = (files) => {
    setFiles((prev) => [...prev, ...files]);
  };

  const handleDeleteFile = (idx) => {
    const newFiles = [...files];
    newFiles.splice(idx, 1);
    setFiles(newFiles);
  };

  const handleSubmit = async () => {
    const [isValid, errors] = validateForm({ conclusion });
    setErrors(errors);
    setTriedToSave(true);
    if (!isValid) {
      enqueueSnackbar({
        variant: "error",
        message: t("resources.missing_required_inputs"),
      });
      return;
    }

    let attachments = [];
    if (files.length > 0) {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("attachments", file);
      });
      const resp = await createAttachment(formData);
      const attachments_id = resp.attachments_id;
      attachments = attachments_id.map((id, idx) => ({
        file: id,
        name: files[idx].name,
        size: files[idx].size,
        type: files[idx].type,
      }));
    }

    const newInterviews = [...interviews];
    const newInterview = newInterviews[idx];

    const dataToSend = {
      conclusion,
      attachments,
    };

    newInterview.conclusion = dataToSend;

    try {
      await handleSaveInterviews(newInterviews);
      enqueueSnackbar({
        variant: "success",
        message: t("resources.successfully_saved"),
      });
      resetValues();
      onHide();
    } catch (error) {
      console.log(error);
    }
  };

  const returnIsValid = (error) => {
    if (!triedToSave) return null;
    if (error) return false;
    return true;
  };

  const resetValues = () => {
    setConclusion("");
    setFiles([]);
    setTriedToSave(false);
    setErrors({});
  };

  return (
    <>
      <AddConclusionWithIA
        show={showAddConclusionWithIA}
        onHide={() => setShowAddConclusionWithIA(false)}
        setConclusion={setConclusion}
        interview={interviews[idx]}
        alias={alias}
        interviewer={interviewer}
      />
      <ModalContainer show={show} onHide={onHide}>
        {/* Header */}
        <Typography
          fontWeight={700}
          fontSize={28}
          color={theme.palette.ethicsBlue.main}
        >
          {t("Agregar conclusiones y/o archivos de la entrevista")}
        </Typography>
        <Typography fontSize={16} color="oxfordBlue">
          {t(
            "Con base en la entrevista y su evaluación, agrega una conclusión como parte del análisis de la investigación."
          )}
        </Typography>

        <Box mt={1.5}>
          <GenerateWithIA
            text={t("Generar conclusión con inteligencia artificial")}
            buttonText={t("AI - GENERAR CONCLUSIÓN")}
            onClick={() => setShowAddConclusionWithIA(true)}
          />
        </Box>

        <Box mt={2}>
          <RichTextEditor
            value={conclusion}
            onChange={setConclusion}
            isValid={returnIsValid(errors?.conclusion)}
          />
        </Box>

        <Box mt={2}>
          <DragAndDropFiles
            text={t("Arrastra y suelta archivos de soporte a la entrevista")}
            onFileSelect={handleAddAttachment}
            multiple
          />
        </Box>

        {files.length > 0 && (
          <Stack direction="row" spacing={1} mt={2} mb={2} flexWrap={"wrap"}>
            {files.map((file, idxFile) => (
              <TemporalUploadedFile
                key={idxFile}
                name={file.name}
                size={file.size}
                iconName="trash_can"
                iconAction={() => handleDeleteFile(idxFile)}
                iconColor={theme.palette.red.main}
              />
            ))}
          </Stack>
        )}

        {/* Buttons */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="flex-end"
          mt={2}
        >
          <Stack direction="row" spacing={2}>
            <InvestigationButton
              label={t("CANCELAR")}
              variant="main"
              outlined
              onClick={onHide}
            />
            <InvestigationButton
              label={t("CONFIRMAR")}
              variant="main"
              onClick={handleSubmit}
            />
          </Stack>
        </Stack>
      </ModalContainer>
    </>
  );
};
